import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAF9F7] flex flex-col font-sans"
      data-testid="forgot-password-page"
    >
      <header className="px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/brand/onebrain-mark-192.png"
            alt=""
            className="w-7 h-7 rounded-md"
          />
          <Wordmark size="md" testid="forgot-header-wordmark" />
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-[#7A7571] hover:text-[#2A2D34] mb-6"
            data-testid="back-to-login"
          >
            <ArrowLeft size={14} />
            Back to sign in
          </Link>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2A2D34]">
            Reset your password
          </h1>
          <p className="font-sans text-sm text-[#3F4148] mt-3 leading-relaxed">
            Enter the email on your onebrain account. If we find a match, we'll
            send a reset link valid for one hour.
          </p>

          {sent ? (
            <div
              className="mt-8 rounded-xl border border-[#1E7BFF]/30 bg-[#F4F8FF] p-5"
              data-testid="forgot-sent-confirmation"
            >
              <p className="font-sans text-sm text-[#2A2D34] leading-relaxed">
                If <strong>{email}</strong> is registered, a reset link is on
                its way. Check your inbox (and spam, just in case).
              </p>
              <Link
                to="/login"
                className="mt-4 inline-block font-sans text-sm font-medium text-[#1E7BFF] hover:text-[#155EC9]"
              >
                Back to sign in →
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label
                  htmlFor="forgot-email"
                  className="block font-sans text-xs text-[#7A7571] mb-1.5"
                >
                  Email address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="forgot-email-input"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2A2D34] focus:outline-none focus:ring-2 focus:ring-[#1E7BFF]/30"
                />
              </div>
              {err ? (
                <p className="font-sans text-sm text-[#8C3A1F]" data-testid="forgot-error">
                  {err}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting || !email.trim()}
                data-testid="forgot-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#1E7BFF] hover:bg-[#155EC9] disabled:opacity-50 text-white font-sans font-medium text-sm px-5 py-3 rounded-lg transition-colors"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={15} />
                ) : (
                  "Send reset link"
                )}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
